import React, {useState} from "react";
import QRCode from "react-qr-code";
import {createPortal} from "react-dom";
import { QrCode2Icon } from "@ui/icons/material/QrCode2";
import {FormattedDateRange} from "@ui/i18n/formatted-date";
import { Trans } from "@ui/i18n/trans";
import { Button } from "@ui/buttons/button";
import { LiveEventScanSession } from "@app/admin/live-events/interfaces/live-event-scan-session";

// Adjust this interface so it matches exactly the structure of `live_event.tickets`
export interface Ticket {
  id: string | number;
  name: string;
  eventName: string;
  participant_name?: string;
  participant_extra_data?: Record<string, any>;  
  starts_at: string; // ISO date string
  ends_at: string; // ISO date string
  uuid: string;
  scanSessions: LiveEventScanSession[];
}

interface TicketListProps {
  /**
   * Array of tickets coming from `live_event.tickets`.
   */
  tickets: Ticket[];
}

const formatSessions = (sessions: LiveEventScanSession[]) => {
  return sessions.map((s, idx) => (
    <React.Fragment key={'date-'+s.id+'-'+idx}>
      {idx > 0 && ", "}
      <FormattedDateRange start={s.start_at} end={s.end_at} preset="short" showYear={false} />
    </React.Fragment>
  ));
};

/**
 * Render a responsive, theme‑aware list of user tickets.
 * Clicking a ticket opens a modal with the ticket UUID as a QR Code.
 */
const TicketList: React.FC<TicketListProps> = ({tickets}) => {
  const [activeUuid, setActiveUuid] = useState<string | null>(null);

  const closeModal = () => setActiveUuid(null);

  return (
    <div className="relative w-full">
      {/* Ticket grid */}
      <div
        role="list"
        className="relative mx-auto w-full"
      >
        {tickets.map((t, idx) => (
          <button
            key={t.id+"-"+idx}
            onClick={() => setActiveUuid(t.uuid)}
            className="flex w-full justify-between items-start border-b py-24"
          >

            <div className="text-start">
              <div className="text-xl font-medium leading-tight">
                {t.eventName} <span className="text-sm text-muted">{" \u2013 "} {t.name}</span>
              </div>
              <div className="text-sm">
                {t.participant_name}
              </div>
              <div className="text-sm text-muted">
                <Trans message="Can be used:"/> {" "}
                {t.scanSessions?.length
                  ? formatSessions(t.scanSessions)
                  : t.starts_at && t.ends_at && (
                      <>
                        <FormattedDateRange
                          start={t.starts_at}
                          end={t.ends_at}
                        />
                      </>
                    )}
              </div>
            </div>
            <QrCode2Icon size="xl"/>
          </button>
        ))}
      </div>

      {/* Modal */}
      {activeUuid &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          >
            <div
              className="max-w-full rounded-2xl bg-white shadow-2xl p-24"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="mb-24 text-center text-2xl font-semibold">
                <Trans message="Your Ticket" />
              </h2>
              <div className="flex justify-center mb-24">
                <QRCode value={activeUuid} size={320} />
              </div>
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={closeModal}
                >Fermer</Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TicketList;
